import React from 'react';
import classnames from 'classnames/bind';
import { Column } from 'react-table';

import { Account } from 'types/account';
import { useCurrentApplication } from 'application/hooks/useCurrentApplication';

import { EmptyView } from 'components/EmptyView/EmptyView';
import {
  PaginatedTable,
  PaginatedTableProps,
} from 'components/Table/PaginatedTable';

import { ApplicationRoleCell, OrganizationRoleCell } from '../Cell';
import { MemberCell } from './Cell';
import { OrganizationRolesHeader, ApplicationRolesHeader } from '../Header';
import styles from '../Table.module.scss';

const c = classnames.bind(styles);

export type MembersTableProps = Pick<
  PaginatedTableProps<Account>,
  'queryResult' | 'pageSize' | 'page' | 'onPageChange'
>;

export function MembersTable(props: MembersTableProps) {
  const { data: application } = useCurrentApplication();
  const applicationRolesHeader = application?.name
    ? `Current Workspace (${application.name})`
    : 'Current Workspace';

  const columns = React.useMemo<Column<Account>[]>(() => {
    return [
      {
        Header: 'Member',
        Cell: MemberCell,
      },
      {
        Header: 'Organization',
        Cell: OrganizationRolesHeader,
        columns: [
          {
            Header: 'Owner',
            Cell(props) {
              return <OrganizationRoleCell props={props} role="owner" />;
            },
          },
          {
            Header: 'Billing',
            Cell(props) {
              return <OrganizationRoleCell props={props} role="billing" />;
            },
          },
          {
            Header: 'Manager',
            Cell(props) {
              return <OrganizationRoleCell props={props} role="manager" />;
            },
          },
        ],
      },
      {
        Header: applicationRolesHeader,
        Cell: ApplicationRolesHeader,
        columns: [
          {
            Header: 'Design',
            Cell(props) {
              return <ApplicationRoleCell props={props} role="designer" />;
            },
          },
          {
            Header: 'Deploy',
            Cell(props) {
              return <ApplicationRoleCell props={props} role="deployer" />;
            },
          },
          {
            Header: 'Monitor',
            Cell(props) {
              return <ApplicationRoleCell props={props} role="monitor" />;
            },
          },
          {
            Header: 'Notifications',
            Cell(props) {
              return <ApplicationRoleCell props={props} role="notification" />;
            },
          },
        ],
      },
    ];
  }, [applicationRolesHeader]);

  return (
    <PaginatedTable<Account>
      {...props}
      id="members-table"
      className={c('table')}
      label="Members"
      columns={columns}
      emptyMessage={<EmptyView>No active members.</EmptyView>}
    />
  );
}
